<template>
  <MainLayout>
    <ContentPageHeader functionName="価格操作管理" pageName="値下げ率一覧" />
    
    <!-- 最終更新日表示 -->
    <div class="d-flex justify-content-end mb-3" v-if="latestUpdateDate">
      <span class="text-muted latest-update-date">最終更新日：{{ latestUpdateDate }}</span>
    </div>
    
    <div class="card mb-4">
      <div class="card-body">
        <PriceDecreaseTable
          :items="tableItems"
          :columns="columns"
          @edit="handleEdit"
        />
      </div>
    </div>
    
    <!-- CommonModalを使用した編集モーダル -->
    <CommonModal
      :visible="showEditModal"
      title="値下げ率編集"
      @close="closeEditModal"
      @ok="saveChanges"
      width="90%"
    >
      <div v-if="editingData">
        <div class="mb-3">
          <label class="form-label">カテゴリ</label>
          <input type="text" class="form-control" :value="editingData.category" disabled />
        </div>
        <div class="mb-3">
          <label class="form-label">販売チャンネル</label>
          <input type="text" class="form-control" :value="getSalesChannelName(editingData.channel.salesChannel)" disabled />
        </div>
        
        <!-- 値下げ率詳細編集 -->
        <div class="mb-3">
          <label class="form-label">値下げ率設定</label>
          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th style="width: 35%">販売価格 (円)</th>
                  <th style="width: 20%">値下げ率 (%)</th>
                  <th style="width: 15%"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(detail, index) in editingDetails" :key="`edit-${index}`">
                  <td>
                    <div class="d-flex align-items-center">
                      <input type="number" class="form-control" v-model="detail.sellingPriceMin" disabled />
                      <span class="mx-2">~</span>
                      <input type="number" class="form-control" v-model="detail.sellingPriceMax" 
                             :id="'priceMax-' + detail.index" 
                             :disabled="index === editingDetails.length - 1"
                             @input="sortedPriceDecrease(detail.index)" />
                    </div>
                  </td>
                  <td>
                    <input type="number" class="form-control" v-model="detail.priceCutRate" />
                  </td>
                  <td>
                    <button v-if="index !== editingDetails.length - 1" class="btn btn-danger" @click="removeDetail(index)">削除</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <button class="btn btn-secondary mt-2" @click="addDetail">行追加</button>
        </div>
        
        <!-- 値下げスパン編集 -->
        <div class="mb-3">
          <label class="form-label">値下げスパン設定 (日数)</label>
          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th style="width: 35%">値下げ日 (日)</th>
                  <th style="width: 15%"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(span, index) in editingSpans" :key="`span-${index}`">
                  <td>
                    <input 
                      type="number" 
                      class="form-control" 
                      v-model="span.priceCutDate" 
                      :id="'price-cut-date-' + span.index" 
                      @input="sortDecreaseDates(span.index)" 
                    />
                  </td>
                  <td>
                    <button class="btn btn-danger" @click="removeSpan(index)">削除</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <button class="btn btn-secondary mt-2" @click="addSpan">行追加</button>
        </div>
      </div>
    </CommonModal>
  </MainLayout>
</template>

<script>
import MainLayout from '@/components/layout/MainLayout.vue'
import ContentPageHeader from '@/components/layout/ContentPageHeader.vue'
import CommonModal from '@/components/common/CommonModal.vue'
import PriceDecreaseTable from './PriceDecreaseTable.vue'
import service from '../../services/service';
import { showLoading, hideLoading } from '@/utils/loadingEventBus';

export default {
  name: 'PriceDecreaseListView',
  components: {
    MainLayout,
    ContentPageHeader,
    CommonModal,
    PriceDecreaseTable
  },
  data() {
    return {
      priceCutList: {},
      salesChannels: [],
      childCategories: [],
      editingData: null,
      editingDetails: [],
      editingSpans: [],
      showEditModal: false,
      columns: [
        { key: 'category', label: 'カテゴリ', minWidth: 150 },
        { key: 'salesChannel', label: '販売チャンネル', minWidth: 150 },
        { key: 'priceRange', label: '販売価格 (円)', minWidth: 150 },
        { key: 'decreaseRate', label: '値下げ率 (%)', minWidth: 100 },
        { key: 'decreaseSpan', label: '値下げスパン', minWidth: 100 },
        { key: 'actions', label: '操作', minWidth: 80 }
      ],
      actions: [],
      latestUpdateDate: '' // APIから取得する値を格納
    }
  },
  computed: {
    tableItems() {
      const items = [];
      
      // priceCutListをテーブル表示用に変換
      Object.entries(this.priceCutList).forEach(([category, channelList]) => {
        // 各カテゴリの最初の行のみカテゴリ名を表示
        let isFirstChannelInCategory = true;
        
        channelList.forEach((channel) => {
          // 価格範囲、値下げ率の配列を作成
          const sortedDetails = [...channel.details].sort((a, b) => a.sellingPriceMin - b.sellingPriceMin);

          const priceRanges = sortedDetails.map(detail => 
            this.formatPriceRange(detail.sellingPriceMin, detail.sellingPriceMax)
          );
          const decreaseRates = sortedDetails.map(detail => detail.priceCutRate);
          
          // 値下げスパンをカンマ区切りの文字列に変換
          const decreaseSpan = 0 < channel.spans.length 
            ? channel.spans.map(span => span.priceCutDate).sort((a, b) => a - b).join(', ')
            : '指定なし';
          
          items.push({
            // 最初の行のみカテゴリ名を表示、それ以外は空文字
            category: isFirstChannelInCategory ? category : '',
            // カテゴリのグループ化のために元のカテゴリ名を保持
            originalCategory: category,
            salesChannel: this.getSalesChannelName(channel.salesChannel),
            priceRange: priceRanges,
            decreaseRate: decreaseRates,
            decreaseSpan: decreaseSpan,
            originalChannel: channel // 編集用に元のデータを保持
          });
          
          // 次の行からはカテゴリ名を表示しない
          isFirstChannelInCategory = false;
        });
      });
      
      return items;
    }
  },
  async mounted() {
    await this.fetchData()
  },
  methods: {
    async fetchData() {
      try {
        showLoading();
        
        // 複数リクエストを並列で実行
        const [channelsResponse, categoriesResponse, priceCutResponse, latestUpdateResponse] = await Promise.all([
          service.getSalesChannelList(),
          service.getCategoryList(),
          service.getPriceCutList(),
          service.getPriceOperationLatestUpdateDate(false)
        ]);
        
        this.salesChannels = channelsResponse.data.salesChannels;
        this.childCategories = categoriesResponse.data.categories.flatMap(category => category.childCategories);
        this.priceCutList = priceCutResponse.data;
        
        // 最終更新日時をセット
        this.latestUpdateDate = latestUpdateResponse && latestUpdateResponse.data ? latestUpdateResponse.data : null;
        
      } catch (error) {
        console.error('データ取得エラー:', error);
        alert('データ取得中にエラーが発生しました。');
      } finally {
        hideLoading();
      }
    },
    getSalesChannelName(id) {
      const channel = this.salesChannels.find(ch => ch.id === id)
      return channel ? channel.acc : ''
    },
    formatPriceRange(min, max) {
      // データが1行かつ下限も上限もnullの場合は"指定なし"
      if ((min === null || min === 0) && (max === null || max === 0)) {
        return '指定なし';
      }
      // 下限がnullで上限がある場合は下限を空白として表示
      if (min === null || min === 0) {
        return `~ ${max}`;
      }
      // 上限がnullの場合
      if (max === null || max === 0) {
        return `${min} ~`;
      }
      // 両方ある場合
      return `${min} ~ ${max}`;
    },
    getCategoryIdByName(name) {
      const category = this.childCategories.find(cat => cat.category === name)
      return category ? category.id : null
    },
    handleEdit(data) {
      const categoryName = data.category;
      const channel = data.channel;
      
      this.editingData = {
        category: categoryName,
        channel,
        categoryId: this.getCategoryIdByName(categoryName)
      }
      
      // 編集用詳細データの作成
      this.editingDetails = 0 < channel.details.length
        ? JSON.parse(JSON.stringify(channel.details))
            .sort((a, b) => a.sellingPriceMin - b.sellingPriceMin)
            .map((item, index) => ({ ...item, index }))
        : [{ index: 0, sellingPriceMin: 1, sellingPriceMax: null, priceCutRate: 0 }]

      
      // 編集用スパンデータの作成
      this.editingSpans = 0 < channel.spans.length
        ? channel.spans
            .slice()
            .sort((a, b) => a.priceCutDate - b.priceCutDate)
            .map((span, index) => ({ ...JSON.parse(JSON.stringify(span)), index }))
        : [];
      
      // モーダルを表示
      this.showEditModal = true
    },
    closeEditModal() {
      this.showEditModal = false
      this.editingData = null
      this.editingDetails = []
      this.editingSpans = []
    },
    addDetail() {
      // 新しい行を追加
      const newIndex = Math.max(...this.editingDetails.map(item => item.index));
      const newItem = {
        index: newIndex + 1,
        id: null,
        sellingPriceMin: null,
        sellingPriceMax: null,
        priceCutRate: 0
      };
      const insertIndex = Math.max(this.editingDetails.length - 1, 0);
      this.editingDetails.splice(insertIndex, 0, newItem);

      this.$nextTick(() => {
        const currentId = `priceMax-${newIndex + 1}`;
        document.getElementById(currentId).focus();
      });
    },
    addSpan() {
      // 新しいスパンを追加
      this.editingSpans.push({
        index: this.editingSpans.length,
        id: null,
        priceCutDate: 0
      })

      this.$nextTick(() => {
        const currentId = `price-cut-date-${this.editingSpans.length - 1}`;
        document.getElementById(currentId).focus();
      });

      
      // 追加後に日付順でソート
      this.sortDecreaseDates();
    },
    removeSpan(index) {
      this.editingSpans.splice(index, 1)
      // 削除後に日付順でソート
      this.sortDecreaseDates()
    },
    
    // 値下げ日を昇順でソートするメソッド
    sortDecreaseDates(index) {
      if (this.editingSpans.length <= 1) return;
      
      // 値下げ日の昇順でソート
      this.editingSpans.sort((a, b) => {
        // nullや空の場合は最大値として扱う（最後に配置）
        if (!a.priceCutDate && a.priceCutDate !== 0) return 1;
        if (!b.priceCutDate && b.priceCutDate !== 0) return -1;
        return a.priceCutDate - b.priceCutDate;
      });

      // フォーカスされていた要素が存在する場合、その要素に再フォーカス
      this.$nextTick(() => {
        if (index === undefined) {
          return;
        }
        const currentId = `price-cut-date-${index}`;
        document.getElementById(currentId).focus();
       });
    },
    /**
     * 値下げ詳細のバリデーションチェックを行う
     * @returns {Object} バリデーション結果 { isValid: boolean, message: string }
     */
    validatePriceCutDetails() {
      // 値下げ率が入力されているかチェック
      for (let i = 0; i < this.editingDetails.length; i++) {
        const detail = this.editingDetails[i]
        
        // 値下げ率が未入力の場合
        if (detail.priceCutRate === null || detail.priceCutRate === undefined || detail.priceCutRate === '') {
          return {
            isValid: false,
            message: `${i+1}行目の値下げ率を入力してください。`
          }
        }
      }

      const maxPriceSet = new Set();
      for (let i = 0; i < this.editingDetails.length; i++) {
        const maxPrice = this.editingDetails[i].sellingPriceMax;
        // 最後の要素以外で、上限金額が未入力ならエラー
        if (i !== this.editingDetails.length - 1 && (maxPrice === null || maxPrice === undefined || maxPrice === '')) {
          return {
            isValid: false,
            message: `${i + 1}行目の上限金額を入力してください。`
          };
        }
        
        // 上限金額の重複エラー
        if (maxPrice !== null && maxPrice !== undefined && maxPrice !== '' && maxPriceSet.has(maxPrice)) {
          return {
            isValid: false,
            message: `上限金額が重複しています。上限金額を正しく設定してください。`
          };
        }
        
        if (maxPrice !== null && maxPrice !== undefined && maxPrice !== '') {
          maxPriceSet.add(maxPrice);
        }
      }
      
      // 値下げ日の未入力チェックと重複チェック
      const dateSet = new Set();
      for (let i = 0; i < this.editingSpans.length; i++) {
        const span = this.editingSpans[i]
        
        // 値下げ日が未入力の場合
        if (span.priceCutDate === null || span.priceCutDate === undefined || span.priceCutDate === '' || isNaN(span.priceCutDate)) {
          return {
            isValid: false,
            message: `${i+1}行目の値下げ日を入力してください。`
          }
        }
        
        // 値下げ日の重複チェック
        if (dateSet.has(span.priceCutDate)) {
          return {
            isValid: false,
            message: `値下げ日が重複しています。値下げ日を正しく設定してください。`
          }
        }
        dateSet.add(span.priceCutDate);
      }
      
      // すべてのチェックをパスした場合
      return { isValid: true, message: '' }
    },

    // 自動ソートと下限金額の自動設定を行うメソッド
    sortedPriceDecrease(index) {
      // 上限金額がnullのデータが2件以上あるか確認
      const nullMaxCount = this.editingDetails.filter(detail => 
        detail.sellingPriceMax === null || detail.sellingPriceMax === undefined || detail.sellingPriceMax === ''
      ).length;
      if (nullMaxCount >= 2) return;
      
      // 1. ソート
      this.editingDetails.sort((a, b) => {
        const aMax = a.sellingPriceMax ?? Infinity;
        const bMax = b.sellingPriceMax ?? Infinity;
        return aMax - bMax;
      });

      // 2. 下限金額を設定
      for (let i = 0; i < this.editingDetails.length; i++) {
        if (i === 0) {
          // 先頭だけ常にnullにする
          this.editingDetails[i].sellingPriceMin = null;
          continue;
        }
        
        const prevMax = this.editingDetails[i - 1].sellingPriceMax;
        this.editingDetails[i].sellingPriceMin = (prevMax != null) ? prevMax + 1 : null;
      }

      // 3.フォーカスされていた要素が存在する場合、その要素に再フォーカス
      this.$nextTick(() => {
        if (index === undefined) {
          return;
        }
        const currentId = `priceMax-${index}`;
        document.getElementById(currentId).focus();
      });
    },
    removeDetail(index) {
      this.editingDetails.splice(index, 1);

      // 配列が1件だけになった場合、その要素のsellingPriceMinをnullにする
      if (this.editingDetails.length === 1) {
        this.editingDetails[0].sellingPriceMin = null;
      }
      this.sortedPriceDecrease();
    },
    async saveChanges() {
      if (!this.editingData || !this.editingData.categoryId) {
        alert('カテゴリ情報が取得できません。')
        return
      }

      // 保存前に自動ソートと下限値の自動設定を行う
      this.sortedPriceDecrease();
      
      // 値下げ日を昇順でソート
      this.sortDecreaseDates();
      
      // 値下げスパンの値を数値型に変換
      this.editingSpans.forEach(span => {
        if (typeof span.priceCutDate === 'string') {
          span.priceCutDate = parseInt(span.priceCutDate, 10)
        }
      })
      
      // バリデーションチェック
      const validation = this.validatePriceCutDetails()
      if (!validation.isValid) {
        alert(validation.message)
        return
      }
      
      // 保存確認ダイアログを表示
      if (!confirm('現在入力されている情報で更新してもよろしいですか？')) {
        return
      }
      
      try {
        showLoading();
        // 値下げ情報を保存
        const result = await service.updatePriceCut(
          this.editingData.categoryId,
          this.editingData.channel.salesChannel,
          this.editingDetails,
          this.editingSpans
        )
        
        if (!result.data.success) {
          alert('値下げ情報の更新に失敗しました。')
          return
        }
        
        // 成功したら一覧を再取得
        await this.fetchData()
        this.closeEditModal()
      } catch (error) {
        console.error('値下げ情報更新エラー:', error)
        alert('値下げ情報の更新中にエラーが発生しました。')
      } finally {
        hideLoading();
      }
    }
  }
}
</script>

<style scoped>
.card {
  margin-bottom: 20px;
}
.card-body {
  margin: 5px;
}

/* モーダルの幅を1.5倍に拡大 */
:deep(.common-modal-content) {
  max-width: 900px !important;
  width: 95% !important;
}

/* 入力フィールドの余白を調整 */
.table td {
  padding: 10px;
}

/* 最終更新日表示のスタイル */
.latest-update-date {
  font-weight: bold;
  padding: 10px 10px 0 0;
}
</style>
